import { ArrowRight, Check, ChevronRight, CircleCheck, Store } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { launchShopifyOAuth } from "@/lib/shopifyOAuth";

const steps = ["Connect Shopify Store", "Configure Return Policy", "Configure Refund Rules", "Invite Team Members", "Choose Subscription Plan", "Finish Setup"];

export default function OnboardingPage() {
  const [step, setStep] = useState(0);
  const [shopDomain, setShopDomain] = useState("");
  const [shopError, setShopError] = useState("");
  const navigate = useNavigate();

  const advanceStep = () => {
    if (step === steps.length - 1) {
      navigate("/app");
      return;
    }
    setStep(step + 1);
  };

  const connectShopify = async () => {
    const normalized = shopDomain.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(normalized)) {
      setShopError("Enter a valid your-store.myshopify.com domain.");
      return;
    }
    setShopError("");
    try {
      const response = await api.get(`/shopify/oauth/start?shop=${encodeURIComponent(normalized)}`);
      launchShopifyOAuth(response.data.authorization_url);
    } catch (error) {
      setShopError(error.response?.data?.detail || "We could not start the Shopify connection. Please try again.");
    }
  };

  return (
    <main className="onboarding-page" data-testid="onboarding-page">
      <header className="onboarding-header">
        <span className="brand"><img className="brand-horizontal-logo" src="/brand/deewel-primary.png" alt="DEEWEL" /></span>
        <span data-testid="onboarding-progress-label">Step {step + 1} of {steps.length}</span>
      </header>
      <div className="onboarding-layout">
        <aside className="onboarding-steps" data-testid="onboarding-steps">
          {steps.map((item, index) => (
            <button key={item} onClick={() => setStep(index)}
              className={index === step ? "onboarding-step-active" : index < step ? "onboarding-step-complete" : ""}
              data-testid={`onboarding-step-${index + 1}`}>
              <span>{index < step ? <Check size={15} /> : index + 1}</span>
              {item}
            </button>
          ))}
        </aside>
        <section className="onboarding-content">
          <span className="onboarding-icon"><Store size={25} /></span>
          <span className="eyebrow">GETTING STARTED</span>
          <h1 data-testid="onboarding-title">{steps[step]}</h1>
          <p data-testid="onboarding-description">
            {step === 0
              ? "Connect your Shopify store to bring your post-purchase operations into DEEWEL."
              : "Set up this part of your workspace now, or fine-tune it later in settings."}
          </p>
          {step === 0 ? (
            <div className="shopify-connect" data-testid="shopify-connect-card">
              <span className="shopify-bag">S</span>
              <div><strong>Connect your Shopify store</strong><small>Secure OAuth connection · Takes less than a minute</small><input value={shopDomain} onChange={(event) => { setShopDomain(event.target.value); setShopError(""); }} placeholder="your-store.myshopify.com" data-testid="shopify-domain-input" />{shopError && <small data-testid="shopify-domain-error">{shopError}</small>}</div>
              <button className="primary-button" onClick={connectShopify} disabled={!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shopDomain.trim().toLowerCase())} data-testid="connect-shopify-button">
                Connect Shopify <ChevronRight size={16} />
              </button>
            </div>
          ) : (
            <div className="onboarding-placeholder" data-testid="onboarding-config-card">
              <CircleCheck size={25} />
              <h2>Ready when you are</h2>
              <p>DEEWEL will apply recommended defaults based on your store configuration.</p>
            </div>
          )}
          <div className="onboarding-actions">
            <button className="text-button" onClick={advanceStep}
              data-testid="skip-onboarding-step-button">Skip for now</button>
            <button className="primary-button" onClick={advanceStep}
              data-testid="continue-onboarding-button">
              {step === steps.length - 1 ? "Finish setup" : "Continue"} <ArrowRight size={16} />
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}